import {
  ACTION_STATUSES,
  CHECKOUT_PATH,
  PATCH_STATUSES_3DS,
  PAYMENT_MINIAPP_POST_MESSAGES,
  TRANSACTION_STATES,
} from 'shared/consts';
import {
  select3DSTransactionState,
  select3DSTransactionToken,
  selectCart,
  selectComplete3DSCartStatus,
  selectSubmitCartStatus,
  selectTriggerTransaction,
} from 'store/cart/selectors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { complete3DSCart } from 'store/cart/actions';
import { replace } from 'connected-react-router';
import { statusUpdates3dSecureSpreedly } from 'store/add-card/actions';
import { useLocation } from 'react-router-dom';
import { Dispatch } from 'redux';
import { Order, TransactionObject, VERTICAL_API_CODES } from 'store/cart/types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { selectGetTransactionStatus, selectTransaction } from 'store/transactions/selectors';
import { useSearchParams } from './use-search-params.hook';
import { useIsSmallViewportWidth } from '@hqo/react-components-library/dist/device';
import { ITEM_TYPE } from 'store/transactions/types';
import qs from 'qs';
import { getTransaction } from 'store/transactions/actions';

export interface Use3DSModalProps {
  setPaymentError: (value: boolean) => void;
  transactionToken: string;
  spreedlyToken: string;
  isQuickCheckout?: boolean;
  currentOffsetHeight?: number;
}
export interface Use3DSModalInterface {
  isFingerprintPending: boolean;
  show3DSModal: boolean;
}

// eslint-disable-next-line max-lines-per-function
export const statusUpdates = (
  event: {
    action: string;
    token: string;
    finalize: (arg0: unknown) => void;
  },
  location: { search: string },
  dispatch: Dispatch,
  setShow3DSModal: React.Dispatch<React.SetStateAction<boolean>>,
  setPaymentError: (value: boolean) => void,
  setIsFingerprintPending: React.Dispatch<React.SetStateAction<boolean>>,
  cart: Order,
  pendingToken: string,
  pendingTransaction?: TransactionObject,
  isQuickCheckout?: boolean,
) => {
  if (cart.transaction.token === event.token) {
    if (event.action === 'succeeded') {
      window.parent.postMessage(
        {
          message: `${PAYMENT_MINIAPP_POST_MESSAGES.PAYMENT_MINIAPP_3DS_STATUS_UPDATE}`,
          value: { transaction_id: cart.transaction_id, status_3ds: PATCH_STATUSES_3DS.SUCCEEDED },
        },
        '*',
      );
      window.parent.postMessage(
        { message: `${PAYMENT_MINIAPP_POST_MESSAGES.PAYMENT_MINIAPP_3DS_SUCCEEDED}`, value: cart.transaction_uuid },
        '*',
      );
      dispatch(replace(`/receipt${location.search}`));
    } else if (event.action === 'error') {
      window.parent.postMessage(
        {
          message: `${PAYMENT_MINIAPP_POST_MESSAGES.PAYMENT_MINIAPP_3DS_STATUS_UPDATE}`,
          value: { transaction_id: cart.transaction_id, status_3ds: PATCH_STATUSES_3DS.FAILED },
        },
        '*',
      );
      window.parent.postMessage(
        { message: `${PAYMENT_MINIAPP_POST_MESSAGES.PAYMENT_MINIAPP_3DS_ERROR}`, value: cart.transaction_uuid },
        '*',
      );
      setShow3DSModal(false);
      setPaymentError(true);
    } else if (event.action === 'challenge') {
      if (isQuickCheckout) {
        window.parent.postMessage(
          {
            message: `${PAYMENT_MINIAPP_POST_MESSAGES.PAYMENT_MINIAPP_QUICK_CHECKOUT_3DS_CHALLENGE}`,
          },
          '*',
        );
      }
      document.getElementById('challenge-modal').classList.remove('hidden');
    } else if (event.action === 'device-fingerprint') {
      document.getElementById('device-fingerprint').classList.remove('hidden');
      setIsFingerprintPending(true);
    } else if (event.action === 'trigger-completion') {
      setIsFingerprintPending(false);
      dispatch(complete3DSCart.request({ transaction_token: pendingToken }));

      if (pendingTransaction?.state === TRANSACTION_STATES.SUCCESS) {
        window.parent.postMessage(
          {
            message: `${PAYMENT_MINIAPP_POST_MESSAGES.PAYMENT_MINIAPP_3DS_STATUS_UPDATE}`,
            value: { transaction_id: cart.transaction_id, status_3ds: PATCH_STATUSES_3DS.SUCCEEDED },
          },
          '*',
        );
        dispatch(replace(`/receipt${location.search}`));
      }
      if (pendingTransaction?.state === TRANSACTION_STATES.PENDING) {
        event.finalize(pendingTransaction);
      }
    }
  }
};

// eslint-disable-next-line max-lines-per-function
export const use3DSModal = ({
  setPaymentError,
  transactionToken,
  spreedlyToken,
  isQuickCheckout,
}: Use3DSModalProps): Use3DSModalInterface => {
  const dispatch = useDispatch();
  const location = useLocation();

  const pendingToken = useSelector(select3DSTransactionToken);
  const submitCartStatus = useSelector(selectSubmitCartStatus);
  const transactionState = useSelector(select3DSTransactionState);
  const complete3DSCartStatus = useSelector(selectComplete3DSCartStatus);
  const cart = useSelector(selectCart);
  const [isFingerprintPending, setIsFingerprintPending] = useState<boolean>(false);
  const [show3DSModal, setShow3DSModal] = useState<boolean>(false);
  const { showMiniappPaymentsNavigation } = useFlags();
  const transaction = useSelector(selectTransaction);
  const transactionStatus = useSelector(selectGetTransactionStatus);
  const { isMobileDevice: isMobile, forPayment, ...restQueryParams } = useSearchParams();
  const isSmallViewportWidth = useIsSmallViewportWidth();
  const isMobileDevice = useMemo(
    () => (isMobile ? isMobile === 'true' : isSmallViewportWidth),
    [isMobile, isSmallViewportWidth],
  );

  const pendingTransaction = useSelector(selectTriggerTransaction);
  const isCartFromResourceBooking = cart?.config?.vertical?.api_code === VERTICAL_API_CODES.RESOURCE_BOOKING;
  const isCartFromServiceBooking = cart?.config?.vertical?.api_code === VERTICAL_API_CODES.SERVICE_BOOKING;

  const statusUpdatesCallback = useCallback(
    // eslint-disable-next-line max-lines-per-function
    (event: { action: string; token: string; finalize: (arg0: unknown) => void }) => {
      statusUpdates(
        event,
        location,
        dispatch,
        setShow3DSModal,
        setPaymentError,
        setIsFingerprintPending,
        cart,
        pendingToken,
        pendingTransaction,
        isQuickCheckout,
      );
    },
    [cart, dispatch, isQuickCheckout, location, pendingToken, pendingTransaction, setPaymentError],
  );

  const createLifecycle = useCallback(() => {
    const lifecycle = new window.Spreedly.ThreeDS.Lifecycle({
      environmentKey: spreedlyToken,
      hiddenIframeLocation: 'device-fingerprint',
      challengeIframeLocation: 'challenge',
      transactionToken,
      challengeIframeClasses: 'SpreedlyIFrameStyle',
    });
    lifecycle.start();
  }, [transactionToken, spreedlyToken]);

  useEffect(() => {
    if (submitCartStatus === ACTION_STATUSES.FULFILLED && transactionState === TRANSACTION_STATES.PENDING) {
      setShow3DSModal(true);
    }
    if (complete3DSCartStatus === ACTION_STATUSES.REJECTED) {
      window.parent.postMessage(
        {
          message: `${PAYMENT_MINIAPP_POST_MESSAGES.PAYMENT_MINIAPP_3DS_STATUS_UPDATE}`,
          value: { transaction_id: cart.transaction_id, status_3ds: PATCH_STATUSES_3DS.FAILED },
        },
        '*',
      );
      setShow3DSModal(false);
    }
  }, [submitCartStatus, complete3DSCartStatus, transactionState, cart]);

  useEffect(() => {
    if (
      showMiniappPaymentsNavigation &&
      isCartFromResourceBooking &&
      submitCartStatus === ACTION_STATUSES.FULFILLED &&
      transactionStatus !== ACTION_STATUSES.PENDING &&
      !transaction
    ) {
      dispatch(getTransaction.request(cart.transaction_uuid));
    }
  }, [
    dispatch,
    submitCartStatus,
    showMiniappPaymentsNavigation,
    cart,
    transactionStatus,
    transaction,
    isCartFromResourceBooking,
  ]);

  // eslint-disable-next-line max-lines-per-function
  useEffect(() => {
    if (submitCartStatus === ACTION_STATUSES.FULFILLED && transactionState === TRANSACTION_STATES.SUCCESS) {
      if (isCartFromResourceBooking) {
        if (showMiniappPaymentsNavigation && transactionStatus === ACTION_STATUSES.FULFILLED && isQuickCheckout) {
          window.parent.postMessage(
            {
              message: `${PAYMENT_MINIAPP_POST_MESSAGES.PAYMENT_MINIAPP_DONE_BUTTON_CLICK}`,
              transactionId: transaction?.details.id,
              transactionUuid: cart.transaction_uuid,
            },
            '*',
          );
        } else if (
          showMiniappPaymentsNavigation &&
          transactionStatus === ACTION_STATUSES.FULFILLED &&
          location.pathname === CHECKOUT_PATH
        ) {
          const queryParams = {
            ...restQueryParams,
            itemId: transaction?.details.id,
            itemType: ITEM_TYPE.RESOURCE_BOOKING,
            forPayment,
          };
          const queryString = qs.stringify(queryParams);
          dispatch(replace(`/historical-receipt?${queryString}`));
        }
        if (!isMobileDevice || !showMiniappPaymentsNavigation) {
          dispatch(replace(`/receipt${location.search}`));
        }
      } else {
        if (showMiniappPaymentsNavigation && isQuickCheckout && isCartFromServiceBooking) {
          window.parent.postMessage(
            {
              message: `${PAYMENT_MINIAPP_POST_MESSAGES.PAYMENT_MINIAPP_DONE_BUTTON_CLICK}`,
              transactionId: transaction?.details.id,
              transactionUuid: cart.transaction_uuid,
            },
            '*',
          );
        }
        dispatch(replace(`/receipt${location.search}`));
      }
    }
  }, [
    submitCartStatus,
    transactionState,
    dispatch,
    location.search,
    location.pathname,
    transactionToken,
    transactionStatus,
    cart,
  ]);

  useEffect(() => {
    if (submitCartStatus === ACTION_STATUSES.FULFILLED && transactionState === TRANSACTION_STATES.PENDING) {
      createLifecycle();
      dispatch(statusUpdates3dSecureSpreedly.request(statusUpdatesCallback));
    }
  }, [submitCartStatus, transactionState, createLifecycle, statusUpdatesCallback]);

  return { isFingerprintPending, show3DSModal };
};
